import { useEffect, useRef, forwardRef, useContext } from "react";
import pLimit from "p-limit";
// @ts-ignore -- react-toastify package.json exports issue
import { toast } from "react-toastify";
import classNames from "classnames";
import prettyBytes from "pretty-bytes";

import { prepAssetDownloadURL } from "../../apiUtils/downloadUrls";
import { useDownloadProgress } from "../../hooks/useDownloadProgress";
import UserContext from "../../contexts/user";
import { AvailableFeatureFlags } from "../../apiClient";
import { hasFeatureFlag } from "../../utils";

const isValidPathSegment = (s: string) => !!s.match("^[a-zA-Z0-9_]+$");
const sanitize = (s: string) => {
  // Drop strings that cause issues on some operating systems (i.e., Windows)
  return s.replace("$RECYCLE.BIN/", "_");
};

const DownloadProgressToastContent = ({
  totalFileCount,
  totalSize,
  toastId,
}: {
  totalFileCount: number;
  totalSize: number;
  toastId: string;
}) => {
  const { remaining } = useDownloadProgress(toastId);
  const [remainingFiles, remainingDataVolume] = remaining;

  useEffect(() => {
    const progress = (totalSize - remainingDataVolume) / totalSize;
    if (remainingDataVolume > 0) {
      toast.update(toastId, { progress });
    } else {
      toast.update(toastId, {
        render: () =>
          `Successfully downloaded ${totalFileCount.toLocaleString()} data assets (${prettyBytes(
            totalSize
          )})`,
        type: "success" as const,
        autoClose: true,
        progress: null,
      });
    }
  }, [toastId, remainingDataVolume, totalFileCount, totalSize]);

  return (
    <>
      <p>
        Downloading {totalFileCount.toLocaleString()} data assets (
        {prettyBytes(totalSize)})
      </p>
      <p>
        {remainingFiles.toLocaleString()} files (
        {prettyBytes(remainingDataVolume)}) remaining...
      </p>
    </>
  );
};

const makeResponseHandler =
  (dir: any, progressCallback: (data: { progress?: number; complete?: any }) => void) => async (response: any, path: string) => {
    let [fileName, ...subdirs] = sanitize(path).split("/").reverse();

    let dirHandle = dir;
    for (let subdir of subdirs.reverse().filter(isValidPathSegment)) {
      try {
        dirHandle = await dirHandle.getDirectoryHandle(subdir, {
          create: true,
        });
      } catch (e) {
        console.error(
          `Error creating directory handle for subdir '${subdir}' of path '${path}': ${e}`
        );
        throw e;
      }
    }

    let fileHandle;
    try {
      fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
    } catch (e) {
      console.error(`Error creating file handle for path '${path}': ${e}`);
      throw e;
    }

    const writeableStream = await fileHandle.createWritable();

    const trackProgress = new TransformStream({
      transform(chunk, controller) {
        progressCallback({ progress: chunk.length });
        controller.enqueue(chunk);
      },
    });

    return response.body.pipeThrough(trackProgress).pipeTo(writeableStream);
  };

const triggerDownloads = async ({
  dataAssetBatchGenerator,
  maxConcurrentDownloads,
  progressCallback,
}: {
  dataAssetBatchGenerator: () => AsyncIterable<any[]>;
  maxConcurrentDownloads: number;
  progressCallback: (data: { progress?: number; complete?: any }) => void;
}) => {
  const dir = await (window as any).showDirectoryPicker({ mode: "readwrite" });
  const handleResponse = makeResponseHandler(dir, progressCallback);

  const triggerDownloadForAsset = (dataAsset: any) =>
    fetch(prepAssetDownloadURL(dataAsset)).then((resp) =>
      handleResponse(resp, dataAsset.path)
        .then(() => progressCallback({ complete: dataAsset }))
        .then(() =>
          console.debug(`Successfully downloaded asset ${dataAsset.path}`)
        )
    );

  const limit = pLimit(maxConcurrentDownloads);
  for await (const batch of dataAssetBatchGenerator()) {
    const concurrencyLimitedDownloadPromises = batch.map((dataAsset: any) =>
      limit(() => triggerDownloadForAsset(dataAsset))
    );
    // eslint-disable-next-line no-use-before-define
    await Promise.all(concurrencyLimitedDownloadPromises);
  }
};

const RapidDownloadButton = ({
  dataAssetBatchGenerator,
  maxConcurrentDownloads = 5,
  className,
  children,
  forwardedRef,
  totalSize,
  totalFileCount,
}: {
  dataAssetBatchGenerator: () => AsyncIterable<any[]>;
  maxConcurrentDownloads?: number;
  className: string;
  children: React.ReactNode;
  forwardedRef: React.Ref<HTMLButtonElement>;
  totalSize: number;
  totalFileCount: number;
}) => {
  const { user } = useContext(UserContext);
  const toastId = useRef(crypto.randomUUID());
  const { setRemaining } = useDownloadProgress(toastId.current);

  useEffect(() => {
    setRemaining([totalFileCount, totalSize]);
  }, [setRemaining, totalSize, totalFileCount]);

  const progressCallback = ({ progress, complete }: { progress?: number; complete?: any }) => {
    if (complete) {
      setRemaining(([remainingFiles, remainingVolume]) => {
        return [remainingFiles - 1, remainingVolume];
      });
    }
    if (progress) {
      setRemaining(([remainingFiles, remainingVolume]) => {
        return [remainingFiles, remainingVolume - progress];
      });
    }
  };

  const triggerDownloadsWithToast = async () => {
    toast(
      <DownloadProgressToastContent
        toastId={toastId.current}
        totalSize={totalSize}
        totalFileCount={totalFileCount}
      />,
      { progress: 0, autoClose: false, toastId: toastId.current }
    );
    try {
      await triggerDownloads({
        dataAssetBatchGenerator,
        maxConcurrentDownloads,
        progressCallback,
      });
    } catch (e) {
      toast.update(toastId.current, {
        render: () => (e as Error).stack,
        type: "error" as const,
      });
      throw e;
    } finally {
      toastId.current = crypto.randomUUID();
    }
  };

  return (
    <button
      type="button"
      ref={forwardedRef}
      className={classNames(
        "ox-button",
        className,
        hasFeatureFlag(user, AvailableFeatureFlags.RAPID_DOWNLOAD)
          ? ""
          : "hidden"
      )}
      onClick={triggerDownloadsWithToast}
    >
      {children}
    </button>
  );
};

const RapidDownloadWithRef = forwardRef((props: any, ref) => (
  <RapidDownloadButton {...props} forwardedRef={ref} />
));
RapidDownloadWithRef.displayName = "RapidDownloadButton";
export default RapidDownloadWithRef;
